import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { GraphemeSvgGlyphPlayer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  DrillPageContext,
  GraphemeDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useDrillPageStrokeViewSize } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.stroke-view-size";
import { View } from "@-/com.npmjs.react-native";
import { useMemo, useState } from "react";
import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

/**
 * A grapheme drill's glyph: drawn once when the front becomes active, then held; it fills the
 * room its layout gives it, down to what the stroke-view size allows.
 */
export function DrillPageGlyphBlock() {
  const { frontVisibleSignal, isActive } = useLearnCardSurface();
  const [character, glyphReplayKey] = useContextSelectors(
    DrillPageContext,
    (c) => c.character,
    (c) => c.glyphReplayKey,
  );
  const [graphemeStrokes] = useContextSelectors(
    GraphemeDrillContext,
    // Share the revealed card's stroke data; the independently cached front slice can still
    // contain a no-strokes result from before a primitive's outlines were added.
    (c) => c.slice.data.graphemeStrokes,
  );
  const [displayMode] = useContextSelectors(NavContext, (c) => c.characterDisplayMode);
  const preferredSize = useDrillPageStrokeViewSize();
  const [availableSize, setAvailableSize] = useState<number | null>(null);
  const strokeViewSize = Math.min(preferredSize, availableSize ?? preferredSize);
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = useMemo(
    () => getGraphemeGlyph(character, displayMode === "font" ? { uncolored: true } : {}),
    [character, displayMode, getGraphemeGlyph],
  );

  const svgStrokeList = useMemo(
    () =>
      // `null` (no stroke data) makes the player render the font glyph instead.
      graphemeStrokes === null
        ? null
        : graphemeStrokes.map(({ median, path }) => ({ median, path })),
    [graphemeStrokes],
  );

  const glyphNode =
    displayMode === "svg" ? (
      <GraphemeSvgGlyphPlayer
        autoPlay={isActive}
        glyph={graphemeGlyph}
        loop={false}
        playSignal={frontVisibleSignal}
        replayKey={glyphReplayKey}
        showBackground={false}
        showStrokeControls={false}
        size={strokeViewSize}
        strokeList={svgStrokeList}
      />
    ) : (
      // Match the SVG glyph's footprint: render the font glyph inside the same square so toggling
      // between modes keeps the character roughly the same visual size. `0.62` is the same fill
      // ratio GraphemeSvgGlyph uses for its own font fallback.
      <GraphemeFontGlyph
        className="text-foreground"
        glyph={graphemeGlyph}
        style={{
          fontSize: Math.round(strokeViewSize * 0.62),
          lineHeight: Math.round(strokeViewSize * 0.62 * 1.2),
        }}
      />
    );

  const glyph = (
    <View
      className="items-center justify-center"
      style={{ height: strokeViewSize, width: strokeViewSize }}
    >
      {glyphNode}
    </View>
  );

  return (
    <View
      className="min-h-0 flex-1 items-center justify-center self-stretch"
      onLayout={({ nativeEvent: { layout } }) => {
        setAvailableSize(Math.max(1, Math.min(layout.width, layout.height)));
      }}
    >
      {glyph}
    </View>
  );
}
